import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock, AlertCircle, Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { AthleteAvatar } from '../components/AthleteAvatar';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { programsAPI, athletesAPI, attendanceAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Attendance = () => {
  const [programs, setPrograms] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [programsRes, athletesRes, attendanceRes] = await Promise.all([
        programsAPI.getAll(),
        athletesAPI.getAll(),
        attendanceAPI.getAll()
      ]);
      
      const loadedPrograms = programsRes.data;
      setPrograms(loadedPrograms);
      setAthletes(athletesRes.data);
      setAttendance(attendanceRes.data);

      if (loadedPrograms.length > 0 && !selectedProgram) {
        setSelectedProgram(loadedPrograms[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error loading data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProgram && selectedDate) {
      const records = {};
      const programAthletes = athletes.filter(a => a.programs.includes(selectedProgram));
      
      programAthletes.forEach(athlete => {
        const existing = attendance.find(
          a => a.athleteId === athlete.id && 
               a.programId === selectedProgram && 
               a.date === selectedDate
        );
        
        records[athlete.id] = existing || {
          athleteId: athlete.id,
          programId: selectedProgram,
          date: selectedDate,
          status: '',
          notes: ''
        };
      });
      
      setAttendanceRecords(records);
    }
  }, [selectedProgram, selectedDate, athletes, attendance]);

  const updateAttendance = (athleteId, field, value) => {
    setAttendanceRecords({
      ...attendanceRecords,
      [athleteId]: {
        ...attendanceRecords[athleteId],
        [field]: value
      }
    });
  };

  const handleSave = async () => {
    try {
      const attendanceArray = Object.values(attendanceRecords).filter(record => record.status);
      
      if (attendanceArray.length === 0) {
        // All statuses cleared — delete existing records for this date/program
        await axios.delete(`${BACKEND_URL}/api/attendance/clear/${selectedProgram}/${selectedDate}`, { withCredentials: true });
        toast({ title: 'Attendance cleared for this date' });
        await fetchData();
        return;
      }

      await attendanceAPI.createBulk(attendanceArray);
      toast({ title: 'Attendance saved successfully!' });
      await fetchData();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast({ title: 'Error saving attendance', variant: 'destructive' });
    }
  };

  const getStatusButton = (athleteId, status, label, IconComponent, color) => {
    const isSelected = attendanceRecords[athleteId]?.status === status;
    return (
      <Button
        variant={isSelected ? 'default' : 'outline'}
        size="sm"
        onClick={() => updateAttendance(athleteId, 'status', status)}
        className={`flex-1 transition-all ${isSelected ? '' : 'hover:border-2'}`}
        style={isSelected ? { backgroundColor: color, borderColor: color } : { borderColor: color, color: color }}
      >
        <IconComponent className="h-4 w-4 mr-1" />
        {label}
      </Button>
    );
  };

  const programAthletes = athletes.filter(a => a.programs.includes(selectedProgram))
    .sort((a, b) => {
      const lastA = (a.name || '').trim().split(/\s+/).pop().toLowerCase();
      const lastB = (b.name || '').trim().split(/\s+/).pop().toLowerCase();
      return lastA.localeCompare(lastB);
    });
  const selectedProgramData = programs.find(p => p.id === selectedProgram);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Take Attendance</h1>
        <p className="text-gray-600 mt-1">Mark attendance for your athletes</p>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-lg font-medium">Loading...</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Selection Controls */}
          <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Select Program</Label>
              <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map(program => (
                    <SelectItem key={program.id} value={program.id}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <div className="relative mt-1">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left appearance-none"
                  style={{ WebkitAppearance: 'none' }}
                />
              </div>
            </div>
          </div>
          {selectedProgramData && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{selectedProgramData.name}</span> • {selectedProgramData.schedule} • {selectedProgramData.time}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attendance List */}
      {programAthletes.length > 0 ? (
        <div className="space-y-4">
          {programAthletes.map(athlete => (
            <Card key={athlete.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between space-y-4 md:space-y-0">
                  <div className="flex items-center space-x-4">
                    <AthleteAvatar athlete={athlete} size="md" style={{ backgroundColor: selectedProgramData?.color || '#0067B9' }} />
                    <div>
                      <h3 className="font-semibold text-gray-800">{athlete.name}</h3>
                      <p className="text-sm text-gray-600">Age {athlete.age}</p>
                    </div>
                  </div>
                  
                  <div className="flex-1 md:ml-8 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {getStatusButton(athlete.id, 'present', 'Present', CheckCircle2, '#007A3D')}
                      {getStatusButton(athlete.id, 'absent', 'Absent', XCircle, '#CE1126')}
                      {getStatusButton(athlete.id, 'late', 'Late', Clock, '#FCD116')}
                      {getStatusButton(athlete.id, 'excused', 'Excused', AlertCircle, '#0067B9')}
                    </div>
                    
                    {attendanceRecords[athlete.id]?.status && (
                      <div>
                        <Textarea
                          placeholder="Add notes (optional)..."
                          value={attendanceRecords[athlete.id]?.notes || ''}
                          onChange={(e) => updateAttendance(athlete.id, 'notes', e.target.value)}
                          className="text-sm"
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={() => setAttendanceRecords({})}>Clear All</Button>
            <Button
              style={{ backgroundColor: '#0067B9' }}
              className="hover:opacity-90"
              onClick={handleSave}
            >
              Save Attendance
            </Button>
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">No athletes in this program</p>
              <p className="text-sm mt-1">Add athletes to this program to take attendance</p>
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
};

export default Attendance;